import twemoji from '@twemoji/api'
import markdownIt from 'markdown-it'
import { bare as emoji } from 'markdown-it-emoji'
import markdownLinkAttributes from 'markdown-it-link-attributes'
import RegexPlugin from 'markdown-it-regexp'
import utils from 'markdown-it-regexp/lib/utils'

import { emoticons, normalizeEmoji } from '@/utils/emojiUtils.js'
import { emojisWithAliases as emojiDefs, emojiUnicodes } from '@/utils/emojis'

// Override to pass the env through...
RegexPlugin.prototype.render = function (tokens, id, options, env) {
  return this.replacer(tokens[id].meta.match, utils, env)
}

const internalBaseURLs = []
if (!window.location.href.startsWith('file:///')) {
  internalBaseURLs.push(window.location.href.split('/#')[0])
}

const mentions = RegexPlugin(
  /@([a-zA-Z0-9_\-.]+)/, // needs to match backend
  (match, utils, env) => {
    if (!env.usersByUsername) return match[0]
    const username = match[1]
    const user = env.usersByUsername[username]
    if (!user) return match[0]
    const url = `/#/user/${user.id}`
    return `
      <a class="mention" href="${utils.escape(url)}">
        @${utils.escape(user.username)}
      </a>`
  },
)

const md = markdownIt('zero', {
  html: false,
  breaks: true,
  linkify: true,
  typopgrapher: true,
  quotes: '“”‘’', // TODO support locale-specific quotes
})
  .use(mentions)
  .use(markdownLinkAttributes, [
    {
      matcher: href => href.startsWith('mailto:'),
      attrs: {
        class: 'fas-after fa-after-envelope',
      },
    },
    {
      matcher: href => href.startsWith('tel:'),
      attrs: {
        class: 'fas-after fa-after-phone',
      },
    },
    {
      matcher: href => internalBaseURLs.find(baseURL => href.startsWith(baseURL)),
      attrs: {},
    },
    {
      // default to external link
      attrs: {
        target: '_blank',
        rel: 'noopener nofollow noreferrer',
        class: 'fas-after fa-after-external-link',
      },
    },
  ])
  .use(emoji, {
    defs: emojiDefs,
    shortcuts: emoticons,
  })
  .enable([
    'heading',
    'emphasis',
    'strikethrough',
    'blockquote',
    'newline',
    'image',
    'link',
    'backticks',
    'linkify',
    'hr',
    'list',
    'fence',
    'code',
  ])

export default {
  render (markdown, env) {
    return renderEmojis(md.render(markdown, env))
  },
  renderInline (markdown, env) {
    return renderEmojis(md.renderInline(markdown, env))
  },
}

/**
 * We want to replace emoji characters with the images
 * which markdown-it-emoji doesn't do, see https://github.com/markdown-it/markdown-it-emoji/issues/43
 * So we just twemoji.parse the whole HTML output
 * If there are emojis inside other html attributes it'll break...
 * ... but we don't support html in our markdown,
 * and the plugins won't generate markdown with emoji attributes
 * Could be nice to make it a plugin though, by doing something like this:
 * https://github.com/makepanic/markdown-it-unicode-emoji/blob/a96a634425d8fe1e434f1736d4bf9268a4b45bd3/lib/replace.js#L90-L127
 *
 */
function renderEmojis (html) {
  return twemoji.parse(html, {
    // One of the uses for this render function is when we paste
    // content into the document editor
    // So that it can detect the emoji to convert into emoji nodes
    // we need to add these data attributes
    // (We could make it so it only does this for this use case not
    // for all emoji image rendering)
    attributes (emoji) {
      const emojiName = emojiUnicodes[normalizeEmoji(emoji)]
      if (!emojiName) return {}
      return {
        'data-id': emojiName,
        'data-type': 'emoji',
      }
    },
  })
}
